import React, { useState } from "react";
import { MuiBox, MuiTypography, SubmitLoaderButton } from "components/atoms";
import { Menu, MenuItem } from "@mui/material";
import { EllipsisVertical, CheckCircle, Clock } from "lucide-react";
import { Button } from "components/ui";
import { useDispatch } from "react-redux";
import { UpdateFeedbackStatus } from "store/middlewares/feedback";
import { openAdminModal, closeAdminModal } from "store/slices/common/adminModal.slice";

/* ---------- confirm: Mark as Resolved ---------- */
const MarkResolvedConfirm = ({ row, filter }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = () => {
    setIsSubmitting(true);
    dispatch(
      UpdateFeedbackStatus({
        id: row.id,
        type: "solved",
        filter,
        onSuccess: () => {
          dispatch(closeAdminModal());
          setIsSubmitting(false);
        },
        onError: () => {
          dispatch(closeAdminModal());
          setIsSubmitting(false);
        },
      }),
    );
  };

  return (
    <div className="grid gap-5 p-1">
      <div className="flex items-center gap-3.5">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
          <CheckCircle className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          Mark this feedback as resolved?
        </h2>
      </div>

      <ul className="grid gap-2">
        <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
          <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
          <span className="break-words">
            The feedback status will be updated to <strong>solved</strong>.
          </span>
        </li>
        <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
          <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
          <span>
            The user will be notified that their feedback has been resolved.
          </span>
        </li>
      </ul>

      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          size="lg"
          disabled={isSubmitting}
          onClick={() => dispatch(closeAdminModal())}
        >
          Cancel
        </Button>
        <SubmitLoaderButton
          type="button"
          size="lg"
          btnText="Mark as Resolved"
          isDisable={isSubmitting}
          onClick={handleConfirm}
        />
      </div>
    </div>
  );
};

/* ---------- confirm: Mark as Pending ---------- */
const MarkPendingConfirm = ({ row, filter }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = () => {
    setIsSubmitting(true);
    dispatch(
      UpdateFeedbackStatus({
        id: row.id,
        type: "pending",
        filter,
        onSuccess: () => {
          dispatch(closeAdminModal());
          setIsSubmitting(false);
        },
        onError: () => {
          dispatch(closeAdminModal());
          setIsSubmitting(false);
        },
      }),
    );
  };

  return (
    <div className="grid gap-5 p-1">
      <div className="flex items-center gap-3.5">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
          <Clock className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          Mark this feedback as pending?
        </h2>
      </div>

      <ul className="grid gap-2">
        <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
          <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
          <span className="break-words">
            The feedback status will be updated to <strong>pending</strong>.
          </span>
        </li>
        <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
          <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
          <span>
            The issue will be marked as unresolved and may need further action.
          </span>
        </li>
        <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
          <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
          <span>You can change this status at any time.</span>
        </li>
      </ul>

      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          size="lg"
          disabled={isSubmitting}
          onClick={() => dispatch(closeAdminModal())}
        >
          Cancel
        </Button>
        <SubmitLoaderButton
          type="button"
          variant="destructive"
          size="lg"
          btnText="Mark as Pending"
          isDisable={isSubmitting}
          onClick={handleConfirm}
        />
      </div>
    </div>
  );
};
/* ----------------------------------------------- */

const Feedback = ({ row, filter }) => {
  const dispatch = useDispatch();

  const ActionMenuArray = [
    row?.status === "pending" && {
      label: "Mark as Resolved",
      icon: <CheckCircle size={16} color="#475569" />,
      onClick: () => {
        dispatch(
          openAdminModal({
            title: "",
            content: <MarkResolvedConfirm row={row} filter={filter} />,
          }),
        );
      },
    },

    row?.status === "solved" && {
      label: "Mark as Pending",
      icon: <Clock size={16} color="#475569" />,
      onClick: () => {
        dispatch(
          openAdminModal({
            title: "",
            content: <MarkPendingConfirm row={row} filter={filter} />,
          }),
        );
      },
    },
  ].filter(Boolean);

  const [actionMenu, setActionMenu] = useState(null);
  const handleActionMenuOpen = (event) => {
    setActionMenu(event.currentTarget);
  };
  const handleActionMenuClose = () => {
    setActionMenu(null);
  };

  return (
    <MuiBox className="flex h-full items-center justify-center gap-3">
      {row?.status === "pending" && (
        <Button
          className="size-7"
          mode="icon"
          variant="ghost"
          onClick={handleActionMenuOpen}
        >
          <EllipsisVertical />
        </Button>
      )}
      <Menu
        anchorEl={actionMenu}
        open={Boolean(actionMenu)}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          " & .MuiMenu-paper": {
            mt: "10px",
            background: "#fff",
            boxShadow:
              "0 4px 6px -1px #e2e8f0, 0 2px 4px -2px #e2e8f0 !important",
          },
        }}
      >
        {ActionMenuArray.map((option) => (
          <MenuItem
            key={option.label}
            onClick={() => {
              handleActionMenuClose();
              option.onClick();
            }}
          >
            <MuiBox sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
              <MuiBox sx={{ width: "fit-content" }}>{option.icon}</MuiBox>
              <MuiTypography
                sx={{ fontWeight: "500", fontSize: "13px", color: "#475569" }}
              >
                {option.label}
              </MuiTypography>
            </MuiBox>
          </MenuItem>
        ))}
      </Menu>
    </MuiBox>
  );
};

export default Feedback;